import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Briefcase, Search, Download, Eye, Clock, CheckCircle, CreditCard, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CLIENT_STATUS_GROUPS, getStatusBadge, isReviewStatus } from '@/lib/statusConfig';
import { getJobs } from '@/lib/dataService';
import { formatDate } from '@/lib/formatters';

const ITEMS_PER_PAGE = 15;

const STATUS_TABS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'In Progress' },
  { id: 'review', label: 'Awaiting You' },
  { id: 'completed', label: 'Completed' },
  { id: 'payment_pending', label: 'Payment Needed' },
  { id: 'on_hold', label: 'On Hold' },
];

const STATUS_GROUPS = CLIENT_STATUS_GROUPS;

const Jobs = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status');

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(statusParam || 'all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (statusParam) setActiveTab(statusParam);
  }, [statusParam]);

  useEffect(() => {
    async function loadJobs() {
      try {
        const data = await getJobs();
        setJobs(data);
      } catch (err) {
        console.error('Failed to load jobs:', err);
      } finally {
        setLoading(false);
      }
    }
    loadJobs();
  }, []);

  // Tab counts
  const tabCounts = useMemo(() => {
    const counts = { all: jobs.length, active: 0, review: 0, completed: 0, payment_pending: 0, on_hold: 0 };
    jobs.forEach(j => {
      for (const [group, statuses] of Object.entries(STATUS_GROUPS)) {
        if (statuses.includes(j.status)) counts[group]++;
      }
    });
    return counts;
  }, [jobs]);

  // Filter, search, sort, paginate
  const processedJobs = useMemo(() => {
    let result = [...jobs];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(j =>
        j.companyName?.toLowerCase().includes(term) ||
        j.jobId?.toLowerCase().includes(term)
      );
    }

    if (activeTab !== 'all') {
      const statuses = STATUS_GROUPS[activeTab] || [];
      result = result.filter(j => statuses.includes(j.status));
    }

    result.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortField === 'createdAt' || sortField === 'date') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else {
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [jobs, searchTerm, activeTab, sortField, sortDir]);

  const totalPages = Math.ceil(processedJobs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedJobs = processedJobs.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, activeTab]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const getActionButton = (job) => {
    const go = (e) => { e.stopPropagation(); navigate(`/jobs/${job.jobId}`); };
    if (job.status === 'completed' || job.status === 'filed') return <Button size="sm" variant="ghost" onClick={go}><Download className="w-3 h-3 mr-1" />Download</Button>;
    if (isReviewStatus(job.status)) return <Button size="sm" onClick={go}><Eye className="w-3 h-3 mr-1" />Review</Button>;
    if (job.status === 'payment_pending') return <Button size="sm" onClick={go}><CreditCard className="w-3 h-3 mr-1" />Pay Now</Button>;
    return <Button size="sm" variant="ghost" onClick={go}><Eye className="w-3 h-3 mr-1" />View</Button>;
  };

  const SortableHeader = ({ field, children }) => (
    <th
      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'text-primary' : 'opacity-40'}`} />
      </div>
    </th>
  );

  return (
    <div data-testid="jobs-page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Jobs</h1>
          <p className="page-subtitle">{jobs.length} total job{jobs.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => navigate('/wizard/start')}>Create New Job</Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { icon: Briefcase, label: 'Total Jobs', count: jobs.length, tab: 'all', bg: 'bg-muted', fg: '' },
          { icon: Clock, label: 'In Progress', count: tabCounts.active, tab: 'active', bg: 'bg-amber-100', fg: 'text-amber-700' },
          { icon: Eye, label: 'Awaiting Review', count: tabCounts.review, tab: 'review', bg: 'bg-muted', fg: 'text-foreground' },
          { icon: CheckCircle, label: 'Completed', count: tabCounts.completed, tab: 'completed', bg: 'bg-green-100', fg: 'text-green-700' },
        ].map(({ icon: Icon, label, count, tab, bg, fg }) => (
          <div
            key={tab}
            className={`bg-card border rounded-lg p-4 cursor-pointer hover:border-foreground transition-colors ${activeTab === tab ? 'border-primary ring-1 ring-primary' : 'border-border'}`}
            onClick={() => setActiveTab(tab)}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>
                <Icon className={`w-5 h-5 ${fg}`} />
              </div>
              <div>
                <p className="text-2xl font-semibold">{loading ? '—' : count}</p>
                <p className="text-sm text-muted-foreground">{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg">
        {/* Tab chips + search */}
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by job ID or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="job-search"
              />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUS_TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  activeTab === t.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {t.label} ({tabCounts[t.id]})
              </button>
            ))}
          </div>
        </div>

        {processedJobs.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No Jobs Found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {activeTab !== 'all' ? 'Try a different filter or ' : ''}create a new job to get started
            </p>
            <Button onClick={() => navigate('/wizard/start')}>Create New Job</Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="jobs-table">
                <thead className="sticky top-0 bg-muted z-10">
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Job ID</th>
                    <SortableHeader field="companyName">Company</SortableHeader>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Service</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <SortableHeader field="createdAt">Created</SortableHeader>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedJobs.map((job) => (
                    <tr
                      key={job.jobId}
                      className="table-row border-b border-border hover:bg-muted/30 cursor-pointer"
                      data-testid="job-row"
                      onClick={() => navigate(`/jobs/${job.jobId}`)}
                    >
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm text-primary hover:underline">{job.jobId}</span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-sm">{job.companyName}</p>
                        {job.fileName && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{job.fileName}</p>}
                      </td>
                      <td className="py-3 px-4 text-sm">{job.serviceType === 'diy' ? 'DIY with AI' : 'Expert Help'}</td>
                      <td className="py-3 px-4">{getStatusBadge(job.status)}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(job.date || job.createdAt)}</td>
                      <td className="py-3 px-4 text-right">{getActionButton(job)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="p-3 border-t border-border flex items-center justify-between bg-muted/30">
                <div className="text-sm text-muted-foreground">
                  Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, processedJobs.length)} of {processedJobs.length}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm px-2">Page {currentPage} of {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Jobs;
